import { Draft } from '@reduxjs/toolkit';
import {
  IEventConditionFieldsFragment,
  IExpressionQueryFieldsFragment,
  IMeasureFieldsFragment,
  QueryType,
  TimeUnit,
} from '../../../../utils/apollo/graphqlSchema';
import { assertCondition, byTypeName } from '../../../../utils/types';
import { INextStepType } from '../../cachedEntities';
import { parseDataTableMeasureToBuilder } from '../../../../utils/builder/measures/parseDataTableMeasureToBuilder';
import { IBuilder, RowType } from '../types';
import { defaultEventCondition, defaultStep } from '../defaults';
import { UnreachableCaseError } from 'ts-essentials';

const getEventConditionsFromMeasure = (
  measure: IMeasureFieldsFragment,
): Array<IEventConditionFieldsFragment[]> => {
  if (measure.__typename === 'SimpleMeasure') {
    return [measure.eventConditions];
  } else if (measure.__typename === 'CompoundMeasure') {
    const { measures } = measure;
    return measures.map((measure) => measure.eventConditions);
  } else {
    throw new UnreachableCaseError(measure);
  }
};

const prepareExpressionToNextStep = (
  draft: Draft<IBuilder>,
  builderInfo: IExpressionQueryFieldsFragment,
  targetType: INextStepType,
  timeUnit: TimeUnit | null,
) => {
  assertCondition(targetType !== QueryType.Expression);
  const { measure, measureProperties } = builderInfo;

  if (targetType === QueryType.Path) {
    const eventConditions = getEventConditionsFromMeasure(measure);
    // In Path we have Starting and Ending events
    // ANY can't be used as starting events
    const startingEventConditions = eventConditions[0]
      // filter all ANY event conditions
      .filter(
        (eventCondition) =>
          eventCondition.events.filter(byTypeName('AnyEvent')).length === 0,
      );

    if (startingEventConditions.length > 0) {
      draft[RowType.paths][0] = startingEventConditions;
    }

    if (eventConditions.length > 1) {
      draft[RowType.paths][1] = eventConditions[eventConditions.length - 1];
    }
  } else if (targetType === QueryType.Retention) {
    const eventConditions = getEventConditionsFromMeasure(measure);

    draft.retention.retentionConditions[0] = eventConditions[0];

    if (eventConditions.length > 1) {
      draft.retention.retentionConditions[1] =
        eventConditions[eventConditions.length - 1];
    }
  } else if (targetType === QueryType.DataTable) {
    if (
      measure.__typename === 'SimpleMeasure' &&
      measureProperties.__typename === 'SimpleMeasureProperties'
    ) {
      draft[RowType.dataTableMeasures] = parseDataTableMeasureToBuilder(
        [measure],
        [measureProperties],
      );
    } else if (
      measure.__typename === 'CompoundMeasure' &&
      measureProperties.__typename === 'CompoundMeasureProperties'
    ) {
      const { measures } = measure;

      draft[RowType.dataTableMeasures] = parseDataTableMeasureToBuilder(
        measures,
        measureProperties.measures,
      );
    }
  } else if (targetType === QueryType.Funnel) {
    const eventConditions = getEventConditionsFromMeasure(measure);

    if (eventConditions.length === 1) {
      eventConditions.push([defaultEventCondition()]);
    }

    draft[RowType.steps] = eventConditions.map((parsedEventCondition) => ({
      ...defaultStep(),
      eventConditions: parsedEventCondition,
    }));
    assertCondition(
      draft.selectedOptions?.type === QueryType.Funnel && timeUnit !== null,
    );
    draft.selectedOptions.timeUnit = timeUnit;
  }
};

export default prepareExpressionToNextStep;
